/** @module api/measurements */

import { apiCall } from '@/api/index'
import {
  apiV1DevicePath,
  apiV1DevicesPath,
  apiV1DownloadsMeasurementsPath,
  apiV1SensorsPath,
  apiV1StatusSensorsPath,
  energyMeasurementsApiV1SensorsPath,
  monthlyCounterMeasurementsApiV1SensorsPath,
  nearestCounterMeasurementsApiV1SensorPath,
  seqsMeasurementsApiV1SensorsPath
} from '@/../lib/routes'
import { DEFAULT_LOCALE } from '@/i18n'

/**
 * API GET to get the sensor Metadata of Emss
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 * @param {string} params.sort (optional) is a sort-string (resource attribute/field name) the returned collection should be sorted by.
 * @param {string} params.locale (optional) is the locale to be used by the BE.
 *
 * @return {promise}
 */
export function getSensorsMetaData({
  ctxt = 'customer',
  fields = [],
  filter = {},
  sort = '',
  locale = DEFAULT_LOCALE
} = {}) {
  const payload = {
    fields,
    filter,
    sort,
    locale
  }

  return apiCall({
    path: apiV1SensorsPath(ctxt),
    payload
  })
}

/**
 * API GET to get the sensor Metadata of Emss
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 *
 * @return {promise}
 */
export function getMonthlyCounterMeasurements({ ctxt = 'customer', filter = {} } = {}) {
  const payload = {
    filter
  }

  return apiCall({
    path: monthlyCounterMeasurementsApiV1SensorsPath(ctxt),
    payload
  })
}

/**
 * API GET to get sensor measurements
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 * @param {string} params.sort (optional) is a sort-string (resource attribute/field name) the returned collection should be sorted by.
 *
 * @return {promise}
 */
export function getSensorsEnergyMeasurements({ ctxt = 'customer', fields = [], filter = {}, sort = '' } = {}) {
  const payload = {
    fields,
    filter,
    sort
  }

  return apiCall({
    path: energyMeasurementsApiV1SensorsPath(ctxt),
    payload
  })
}

/**
 * API GET to get nearest sensor measurements
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.sensor sensor to request
 * @param {object} params.date the time point to find the nearest value
 *
 * @return {promise}
 */
export function getSensorsEnergyNearestMeasurments({ ctxt = 'customer', sensor = '', datetime = '' } = {}) {
  const payload = {
    filter: {
      datetime
    }
  }

  return apiCall({
    path: nearestCounterMeasurementsApiV1SensorPath(ctxt, sensor),
    payload
  })
}

/**
 * API GET to get devices
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 * @param {string} params.locale (optional) is the locale to be used by the BE.
 *
 * @return {promise}
 */
export function getDeviceMetaData({ ctxt = 'customer', fields = [], filter = {}, locale = DEFAULT_LOCALE } = {}) {
  const payload = {
    fields,
    filter,
    locale
  }

  return apiCall({
    path: apiV1DevicesPath(ctxt),
    payload
  })
}

/**
 * API PATCH to the change the name for a device
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the device's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.name is the new name
 *
 * @return {promise} with resolves for the returned group
 */
export function updateDevice({ id, ctxt = 'customer', givenName = '' } = {}) {
  const payload = {
    data: {
      type: 'devices',
      id,
      attributes: {
        givenName
      }
    }
  }

  return apiCall({
    method: 'PATCH',
    path: apiV1DevicePath(ctxt, id),
    payload
  })
}

/**
 * Function to get the downloadPath
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the call
 * @param {string}Array params.sensorIds are the desired Ids of the Sensors
 * @param {sting} params.sequenceKind is the datatype ('avg', 'power', 'energy', 'bare_meter')
 * @param {integer} params.countNum is the number of Sensors with this 'emsId.sensorTypeId' combination
 * @param {integer} params.flag is the kind of the sensor 'e.g instant, ref...'
 * @param {object} params.tFilter chosen timefilter
 * @param {string} params.resolution picked time resolution ('minute',...)
 * @param {string} params.tz the timezone for the datacall
 * @param {string} params.header the headerlabel (HUMANIZED || ID)
 * @param {integer} params.preview wanted header(positive)||tail(negative) preview length
 *
 */
export function getDownloadPath({
  ctxt = 'customer',
  sensorIds = null,
  sequenceKind = 'energy',
  tFilter = {},
  resolution = null,
  tz = 'utc',
  header = null,
  preview = null,
  downloadId = null,
  colSep = null,
  decSep = null
}) {
  const filter = {
    resolution,
    sensorIds,
    sequenceKind,
    tFilter,
    tz
  }

  if (header) {
    filter.header = header
  }
  if (preview) {
    filter.preview = preview
  }

  if (sensorIds && sensorIds.length > 0) {
    filter.sensorIds = sensorIds.join(',')
  }

  const payload = { colSep, decSep, downloadId, filter }

  const searchParams = $.param(payload)

  return `${apiV1DownloadsMeasurementsPath(ctxt)}?${searchParams}`
}

/**
 * Function to get the downloadPath
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the call
 * @param {string} Array params.sensorIds are the desired Ids of the Sensors
 * @param {string} params.resolution picked time resolution ('minute',...)
 * @param {object} params.tFilter chosen timefilter {dateFrom, dateTo}
 * @param {integer} params.type data type
 *
 */
export function getMeasurementSequences({
  ctxt = 'customer',
  sensorIds = null,
  resolution = null,
  tFilter = {},
  type = 'power_mm_counter_seqs'
}) {
  const filter = {
    resolution,
    sensorIds,
    tFilter
  }

  if (sensorIds && sensorIds.length > 0) {
    filter.sensorIds = sensorIds.join(',')
  }

  return apiCall({
    path: seqsMeasurementsApiV1SensorsPath(ctxt, type),
    payload: { filter }
  })
}

export function getStatusSensorsMetaData({ ctxt = 'customer', filter = {} } = {}) {
  const payload = {
    filter,
    include: 'device'
  }

  return apiCall({
    path: apiV1StatusSensorsPath(ctxt),
    payload
  })
}
